import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { WebhookRegistration } from "../api";

import Icons from "@/components/icons";
import { Badge } from "@/components/ui/v2/badge";
import { KeyValueCardContent } from "@/components/ui/v2/key-value-card-content";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";

interface ClientWebhookConfigurationProps {
  webhookId: string | null;
  webhookSecret: string | null;
  matchingRegistration?: WebhookRegistration | null;
}

export function ClientWebhookConfiguration({ webhookId, webhookSecret, matchingRegistration }: ClientWebhookConfigurationProps) {
  const { t } = useTranslation();

  const hasMismatch = matchingRegistration?.isMismatched ?? false;

  if (!webhookId && !matchingRegistration) {
    return null;
  }

  const rows: Array<{ label: string; value: ReactNode }> = [
    {
      label: t("webhooks.recruitnow.webhookId"),
      value: webhookId ? <span className="font-mono text-sm">{webhookId}</span> : null,
    },
  ];
  if (matchingRegistration?.webHookUri) {
    rows.push({
      label: t("webhooks.recruitnow.webhookUri"),
      value: (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-sm truncate block cursor-help">
              {matchingRegistration.webHookUri}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs break-all">{matchingRegistration.webHookUri}</p>
          </TooltipContent>
        </Tooltip>
      ),
    });
  }
  if (webhookSecret) {
    rows.push({
      label: t("webhooks.recruitnow.webhookSecret"),
      value: (
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className={`font-mono text-sm truncate block cursor-help ${matchingRegistration?.isSecretMismatched ? "text-destructive" : ""}`}
            >
              {webhookSecret}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p className="max-w-xs break-all">{webhookSecret}</p>
          </TooltipContent>
        </Tooltip>
      ),
    });
  }

  const badge = hasMismatch ? (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>
          <Badge variant="warning" size="medium">
            <Icons.WarningIcon fontSize={16} />
            {t("webhooks.recruitnow.status.mismatch")}
          </Badge>
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-md">
        <div className="flex flex-col gap-1">
          {matchingRegistration?.isSecretMismatched && (
            <div>{t("webhooks.recruitnow.secretMismatch")}</div>
          )}
          {matchingRegistration?.isUriMismatched && (
            <div>{t("webhooks.recruitnow.uriMismatch")}</div>
          )}
          {matchingRegistration?.isFiltersMismatched && (
            <div>{t("webhooks.recruitnow.filtersMismatch")}</div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  ) : undefined;

  return <KeyValueCardContent rows={rows} badge={badge} />;
}
